import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { ChartData } from 'chart.js';
import styled from 'styled-components';

import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { OwnershipType } from '@/shared/lib/sj-orm/models/asset.dto';

import { BarStackedChart } from './charts/BarStackedChart';
import { DashboardItem } from './DashboardItem';
import { Legend, TextContainer } from './styled';

export const OWNERSHIP_CONFIG = {
  [OwnershipType.DIRECT]: {
    translationKey: 'totalInRepoChart.direct',
    color: COLORS.systemSky200,
    emptyValue: 37,
  },
  [OwnershipType.INDIRECT]: {
    translationKey: 'totalInRepoChart.indirect',
    color: COLORS.colorDemoCharts[3],
    emptyValue: 4,
  },
  NOT_SPECIFIED: {
    translationKey: 'totalInRepoChart.notSpecified',
    color: COLORS.systemDark100,
    emptyValue: 0,
  },
};

export type OwnershipKey = keyof typeof OWNERSHIP_CONFIG;

export const TotalAssetsInfo = () => {
  const { t } = useTranslation('pages/pageInterwill', { keyPrefix: 'dashboard' });
  const sjDb = useSJDatabase();

  const assets = useMemo(() => {
    return sjDb.collections.assets?.findMany(() => true);
  }, [sjDb]);

  const hasData = Boolean(assets && assets?.length);

  const chartDetails = useMemo(() => {
    const counts: Record<OwnershipType | 'NOT_SPECIFIED', number> = {
      DIRECT: 0,
      INDIRECT: 0,
      NOT_SPECIFIED: 0,
    };

    if (assets?.length) {
      for (const asset of assets) {
        const key = asset.ownerShipType ?? 'NOT_SPECIFIED';
        if (key in counts) {
          counts[key]++;
        }
      }
    }

    return (Object.keys(OWNERSHIP_CONFIG) as OwnershipKey[]).map((ownershipType) => ({
      ownershipType,
      value: hasData ? counts[ownershipType] : OWNERSHIP_CONFIG[ownershipType].emptyValue,
    }));
  }, [assets, hasData]);

  const chartData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: chartDetails.map((item) => t(OWNERSHIP_CONFIG[item.ownershipType].translationKey)),
      datasets: chartDetails.map(({ value, ownershipType }) => ({
        label: t('totalInRepoChart.title'),
        data: [value],
        backgroundColor: OWNERSHIP_CONFIG[ownershipType].color,
        barPercentage: 2,
        categoryPercentage: 1.0,
      })),
    }),
    [chartDetails, t],
  );

  return (
    <DashboardItem hasData={hasData}>
      <BarChartWrapper>
        <BarStackedChart data={chartData} />
      </BarChartWrapper>
      <Legend>
        <p>{t('totalInRepoChart.title')}</p>
        {chartDetails.map((item) => {
          if (item.ownershipType === 'NOT_SPECIFIED' && item.value < 1) return null;
          return (
            <TextContainer key={item.ownershipType}>
              <Icon $bgColor={OWNERSHIP_CONFIG[item.ownershipType].color} />
              <p>{t(OWNERSHIP_CONFIG[item.ownershipType].translationKey)}:</p>
              <Value>{item.value}</Value>
            </TextContainer>
          );
        })}
      </Legend>
    </DashboardItem>
  );
};

const BarChartWrapper = styled.div`
  width: 58px;
  min-height: 135px;
  height: 100%;
  position: relative;

  ${media.iwContainer.s} {
    margin-inline: 38px;
  }
`;

const Icon = styled.div<{ $bgColor: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $bgColor }) => $bgColor};
`;

const Value = styled.p`
  color: ${COLORS.systemDark300};
`;
